import { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Space, Upload } from 'antd';
import Title from 'antd/lib/typography/Title';
import { UploadOutlined } from '@ant-design/icons';

import { Vendor } from '../../modules/vendors';
import { normFile, checkPersonalPhoneNumber, checkRRN } from '../../util/utils';
import TextArea from 'antd/lib/input/TextArea';

const { Option } = Select;

interface VendorWorkerCreateUpdateComponentProps {
  vendor?: Vendor;
  vendorConfig: any;
  invoiceCommon: any;
  uploadProps: any;
  onCreate: (data: any) => void;
  onCancel: () => void;
}

function VendorWorkerCreateUpdateComponent({
  vendor,
  vendorConfig,
  invoiceCommon,
  uploadProps,
  onCreate,
  onCancel,
}: VendorWorkerCreateUpdateComponentProps) {
  const [form] = Form.useForm();
  const [mount, setMount] = useState(true);
  const [jigjongOptions, setJigjongOptions] = useState<any[]>();
  const [categoryList, setCategoryList] = useState<any[]>();
  const [gongjongList, setGongjongList] = useState<any[]>();

  let title: string, application: any;

  useEffect(() => {
    if (!mount) return;
    let children: any[] = [];
    vendorConfig.jigjongs.sort((a: any, b: any) =>
      a.name.localeCompare(b.name),
    );

    for (let i = 0; i < vendorConfig.jigjongs.length; i++) {
      children.push(
        <Option key={i} value={vendorConfig.jigjongs[i].name}>
          {vendorConfig.jigjongs[i].name}
        </Option>,
      );
    }
    setJigjongOptions(children);

    children = [];
    for (const category of invoiceCommon.categoryList.sort((a: any, b: any) =>
      a.name.localeCompare(b.name),
    )) {
      children.push(
        <Option key={category.id} value={category.name}>
          {category.name}
        </Option>,
      );
    }
    setCategoryList(children);

    children = [];
    for (const gongjong of invoiceCommon.gongjongList.sort((a: any, b: any) =>
      a.name.localeCompare(b.name),
    )) {
      children.push(
        <Option key={gongjong.id} value={gongjong.name}>
          {gongjong.name}
        </Option>,
      );
    }
    setGongjongList(children);

    return () => setMount(false);
  }, [vendorConfig, mount, invoiceCommon]);

  if (!!vendor) {
    title = '작업자 수정';
    application = {
      ...vendor.application,
    };
  } else {
    title = '작업자 생성';
    application = {};
  }

  return (
    <>
      <Row>
        <Col span={12}>
          <Title level={3}>{title}</Title>
        </Col>
      </Row>
      <Form
        form={form}
        layout="vertical"
        onFinish={onCreate}
        // initialValues={_application}
        initialValues={{
          application,
          gongjong: vendor?.gongjong,
          category: vendor?.category,
          description: vendor?.description,
        }}
      >
        <Title level={5}>일반현황</Title>
        <Row justify="space-between">
          <Col span={5}>
            <Form.Item
              name={['application', 'general', 'name']}
              label="이름"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input placeholder="예) 홍길동" />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name={['application', 'general', 'residentNumber']}
              label="주민등록번호"
              rules={[
                {
                  validator: (_, value) => checkRRN(value),
                },
              ]}
            >
              <Input placeholder="예) 6406114210911" />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name={['application', 'general', 'address']}
              label="주소"
            >
              <Input placeholder="예) 청주시 청원구 오창읍 오창중앙로 123-12" />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name={['application', 'general', 'personalPhoneNumber']}
              label="휴대전화번호"
              rules={[
                {
                  validator: (_, value) => checkPersonalPhoneNumber(value),
                },
              ]}
            >
              <Input type="text" placeholder="예) 01051629415" />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={5}>
            <Form.Item
              name={['application', 'general', 'jigjong']}
              label="직종"
            >
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="직종을 선택 하세요."
              >
                {jigjongOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name={['application', 'general', 'jobLevel']}
              label="급여수준"
            >
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="급여수준을 입력하세요"
              >
                <Option value="1">기공</Option>
                <Option value="2">조공</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name={['application', 'general', 'wageForm']}
              label="급여방식"
            >
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="급여방식을 입력하세요."
              >
                <Option value="1">일당</Option>
                <Option value="2">프리랜서</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name="attachedFiles"
              label="첨부파일"
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined rev={undefined} />}>
                  첨부파일 올리기
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={11}>
            <Form.Item name="description" label="설명">
              <TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>

        <Title level={5}>계좌정보</Title>
        <Row justify="space-between">
          <Col span={5}>
            <Form.Item
              name={['application', 'accountInfo', 'bankName']}
              label="은행명"
            >
              <Input placeholder="예) 국민은행" />
            </Form.Item>
          </Col>
          <Col span={1}></Col>
          <Col span={5}>
            <Form.Item
              name={['application', 'accountInfo', 'accountHolder']}
              label="예금주"
            >
              <Input placeholder="예) 홍길동" />
            </Form.Item>
          </Col>
          <Col span={1}></Col>
          <Col span={5}>
            <Form.Item
              name={['application', 'accountInfo', 'accountNumber']}
              label="계좌번호"
            >
              <Input placeholder="예) 74391013412345" />
            </Form.Item>
          </Col>
          <Col span={5}></Col>
        </Row>

        <Title level={5}>비용 설정</Title>
        <Row>
          <Col span={5}>
            <Form.Item name="gongjong" label="공정">
              <Select style={{ width: '100%' }} allowClear>
                {gongjongList}
              </Select>
            </Form.Item>
          </Col>
          <Col span={1}></Col>
          <Col span={5}>
            <Form.Item name="category" label="용도">
              <Select style={{ width: '100%' }}>{categoryList}</Select>
            </Form.Item>
          </Col>
        </Row>

        <Row justify="center">
          <Form.Item>
            <Space size="middle">
              <Button htmlType="button" onClick={onCancel}>
                취소
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: '100px' }}
              >
                확인
              </Button>
            </Space>
          </Form.Item>
        </Row>
      </Form>
    </>
  );
}

export default VendorWorkerCreateUpdateComponent;
